import type { UnifiedFilter } from '../components/generic/FilterSection';

/** Prefix for sessionStorage keys — used by every module whose list uses {@link GenericListPage}. */
export const LIST_STATE_STORAGE_PREFIX = 'ums:listState:' as const;

/**
 * Stable key per list screen so filters/search do not collide between modules.
 * Pass `config.module.route.list` from the module config (e.g. `/clients`, `/crm-tasks`).
 */
export function getListStateStorageKey(listRoute: string): string {
  return `${LIST_STATE_STORAGE_PREFIX}${listRoute}`;
}

export interface GenericListPersistedState {
  searchTerm: string;
  unifiedFilters: Record<string, UnifiedFilter>;
  activeFilterTab: string;
  filtersVisible: boolean;
}

/** Restores search + advanced filters when returning from edit/detail (see persistGenericListState). */
export function loadPersistedGenericListState(
  storageKey: string,
  defaultFilterTab: string
): GenericListPersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    return {
      searchTerm: typeof p.searchTerm === 'string' ? p.searchTerm : '',
      unifiedFilters:
        p.unifiedFilters && typeof p.unifiedFilters === 'object' && !Array.isArray(p.unifiedFilters)
          ? (p.unifiedFilters as Record<string, UnifiedFilter>)
          : {},
      activeFilterTab: typeof p.activeFilterTab === 'string' ? p.activeFilterTab : defaultFilterTab,
      filtersVisible: typeof p.filtersVisible === 'boolean' ? p.filtersVisible : false,
    };
  } catch {
    return null;
  }
}

export function persistGenericListState(storageKey: string, state: GenericListPersistedState): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // quota / private mode
  }
}
